import { type ReactNode } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { Image } from "../../image/src/image";
import { Link } from "../../link/src/link";
import { Lozenge, type LozengeAppearance } from "../../lozenge/src/lozenge";

export interface BlogNewsCardProps {
    color: BlogNewsCardColor;
    label: string;
    title: string;
    link: {
        href: string;
        text: string;
    };
    image: {
        src: string;
        alt: string;
    };
    /** Slot rendered between the lozenge and the title (used by BlogEventCard). */
    eventMeta?: ReactNode;
}

export type BlogNewsCardColor = "purple" | "green" | "orange" | "blue";

export const BlogNewsCard = (props: BlogNewsCardProps) => {
    const { color, label, title, link, image, eventMeta } = props;

    const bgColor = colorToToken[color];

    return (
        <a href={link.href} css={cardStyles}>
            <Image
                src={image.src}
                alt={image.alt}
                className="blog-news-card-image"
            />
            {/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
            <div css={contentStyles} style={{ backgroundColor: bgColor }}>
                <Lozenge appearance={colorToLozenge[color]} text={label} />
                {eventMeta}
                <div css={titleStyles} className="card-title-sm">
                    {title}
                </div>
                <div className="link-md">
                    <Link
                        type="primary"
                        bgColor="bgBlackBlue"
                        href={link.href}
                        text={link.text}
                        aria-label={link.text}
                    />
                </div>
            </div>
        </a>
    );
};

const colorToToken: Record<BlogNewsCardColor, string> = {
    purple: wacToken("color.extended.purple800"),
    green: wacToken("color.extended.lime800"),
    orange: wacToken("color.extended.saffron800"),
    blue: wacToken("color.extended.blue800")
};

const colorToLozenge: Record<BlogNewsCardColor, LozengeAppearance> = {
    purple: "purple",
    green: "lime",
    orange: "saffron",
    blue: "blue"
};

const cardStyles = css({
    "borderRadius": wacToken("corner.radius.card"),
    "overflow": "hidden",
    "display": "flex",
    "flexDirection": "column",
    "textDecoration": "none",
    "color": "inherit",
    "flexShrink": 0,
    [media.above.sm]: {
        width: "368px"
    },
    [media.above.lg]: {
        width: "528px"
    },
    "width": "280px",
    "height": "fit-content",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".blog-news-card-image": {
        width: "100%",
        aspectRatio: "1 / 1",
        objectFit: "cover",
        display: "block"
    }
});

const contentStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: token("space.250"),
    borderEndStartRadius: "inherit",
    borderEndEndRadius: "inherit",
    padding: `${token("space.300")} ${token("space.200")}`,
    [media.above.sm]: {
        padding: `${token("space.400")} ${token("space.300")}`
    },
    [media.above.md]: {
        padding: token("space.400")
    },
    [media.above.lg]: {
        padding: token("space.500")
    }
});

const titleStyles = css({
    "&.card-title-sm": {
        color: wacToken("color.neutral.light0")
    }
});
